import { useEffect } from "react";
import { BookOpen, Utensils, MessageCircleHeart } from "lucide-react";
import EmailSignup from "@/components/EmailSignup";

const PILLARS = [
  {
    icon: Utensils,
    title: "Recipes you'll actually make",
    body: "Real-kitchen, real-ingredient recipes — tested at home, written for yours. Nothing fussy, nothing wasteful.",
  },
  {
    icon: BookOpen,
    title: "How-tos & techniques",
    body: "The science of why your wings stay soggy and how to fix it. Practical tips you can use tonight.",
  },
  {
    icon: MessageCircleHeart,
    title: "Honest gear advice",
    body: "We only recommend the tools we actually use. If we wouldn't buy it again, you won't see it here.",
  },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "About — JustAirFry";
  }, []);

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="py-14 md:py-20" style={{ backgroundColor: "var(--jaf-surface)" }}>
        <div className="jaf-container grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span
              className="inline-block text-xs tracking-[0.18em] uppercase font-semibold mb-3"
              style={{ color: "var(--jaf-primary)" }}
            >
              Our story
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Hi, we're <span style={{ color: "var(--jaf-primary)" }}>JustAirFry</span> — and we believe great food shouldn't be complicated.
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed" style={{ color: "var(--jaf-text-muted)" }}>
              What started as a single chicken-wing experiment on a rainy Tuesday became a slightly obsessive love letter to the air fryer — and to home cooks who just want dinner to be easier, tastier, and a little more joyful.
            </p>
          </div>

          <div className="relative">
            <div
              className="absolute -inset-5 rounded-[2rem] -z-10"
              style={{ backgroundColor: "rgba(124, 144, 130, 0.18)" }}
            />
            <img
              src="https://images.unsplash.com/photo-1636647511729-6703539ba71f?w=1200&q=80"
              alt="Home cook in a warm kitchen"
              className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              data-testid="about-hero-image"
            />
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-16 md:py-24">
        <div className="jaf-container max-w-3xl">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">A family kitchen, gone digital</h2>
          <div className="mt-6 space-y-5 text-base sm:text-lg leading-relaxed" style={{ color: "var(--jaf-text-muted)" }}>
            <p>
              JustAirFry is a small kitchen blog. Every recipe you read here started on our counter, was tested at least three times, and got served to people we love before it ever made it online.
            </p>
            <p>
              We're not chefs. We're parents, hosts, weeknight problem-solvers, and obsessive notetakers. We figured out the difference between a soggy salmon and a perfect one — and we wrote it down so you wouldn't have to.
            </p>
            <p>
              No two-page life stories before the recipe. No 47 ingredients you'd buy once and never use again. Just good food, gentle guidance, and a warm corner of the internet to come back to.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-16 md:py-20" style={{ backgroundColor: "var(--jaf-surface)" }}>
        <div className="jaf-container">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight max-w-2xl">
            What you'll find here
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {PILLARS.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                data-testid={`pillar-${title.split(" ")[0].toLowerCase()}`}
                className="bg-white rounded-xl p-7"
                style={{ border: "1px solid var(--jaf-border)" }}
              >
                <span
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full mb-5"
                  style={{
                    backgroundColor: "rgba(200, 90, 50, 0.1)",
                    color: "var(--jaf-primary)",
                  }}
                >
                  <Icon size={22} />
                </span>
                <h3 className="font-serif text-2xl mb-2">{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--jaf-text-muted)" }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing note */}
      <section className="py-16">
        <div className="jaf-container text-center max-w-2xl">
          <p className="font-serif text-3xl sm:text-4xl" data-testid="closing-note">
            Cook well. <span style={{ color: "var(--jaf-primary)" }}>Eat well.</span> Share generously.
          </p>
          <p className="mt-3 text-sm" style={{ color: "var(--jaf-text-muted)" }}>
            That's the whole philosophy. Thanks for being here.
          </p>
        </div>
      </section>

      {/* Email CTA */}
      <section className="py-16 md:py-20">
        <div className="jaf-container">
          <EmailSignup source="about" testIdPrefix="about-signup" />
        </div>
      </section>
    </div>
  );
}
